import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, X, Upload, FileText, Plus, Trash2 } from "lucide-react";
import { api, errMsg, fileUrl, photoUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const refCache = {};
export function useRef(collection) {
  const [items, setItems] = useState(refCache[collection] || []);
  useEffect(() => {
    if (!collection) return;
    const url = collection === "_users" ? "/admin/assignees" : `/admin/c/${collection}`;
    api.get(url, { params: collection === "_users" ? {} : { limit: 1000 } }).then((r) => { refCache[collection] = r.data; setItems(r.data); }).catch(() => {});
  }, [collection]);
  return items;
}

const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";

async function uploadFiles(files, visibility) {
  const out = [];
  for (const f of files) {
    const fd = new FormData();
    fd.append("file", f);
    fd.append("visibility", visibility);
    try { out.push((await api.post("/admin/upload", fd)).data); } catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
  }
  return out;
}

function Images({ value = [], onChange, tid, single }) {
  const [busy, setBusy] = useState(false);
  const list = single ? (value && typeof value === "string" ? [value] : []) : (Array.isArray(value) ? value : []);
  const add = async (e) => {
    setBusy(true);
    const up = await uploadFiles([...e.target.files], "public");
    setBusy(false);
    const mapped = up.map((u) => ({ path: u.path, url: u.url }));
    if (single) onChange(mapped[0]?.url || value); else onChange([...list, ...mapped]);
  };
  const move = (i, d) => { const n = [...list]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  return (
    <div className="space-y-2" data-testid={tid}>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {list.map((p, i) => (
          <div key={i} className="relative aspect-[4/3] rounded-lg overflow-hidden bg-slate-100 group">
            <img src={typeof p === "string" ? fileUrl(p) : photoUrl(p)} alt="" className="w-full h-full object-cover" />
            {i === 0 && !single && <span className="absolute top-1 left-1 text-[10px] bg-[#FF3333] text-white px-1.5 rounded">Principal</span>}
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/50 p-1">
              {!single && <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="text-white disabled:opacity-30"><ArrowLeft size={14} /></button>}
              <button type="button" onClick={() => onChange(single ? "" : list.filter((_, k) => k !== i))} className="text-white" data-testid={`${tid}-remove-${i}`}><X size={14} /></button>
              {!single && <button type="button" disabled={i === list.length - 1} onClick={() => move(i, 1)} className="text-white disabled:opacity-30"><ArrowRight size={14} /></button>}
            </div>
          </div>
        ))}
      </div>
      {single && <Input placeholder="o pega una URL (/images/... o https://...)" value={value || ""} onChange={(e) => onChange(e.target.value)} />}
      <label className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer rounded-md border border-dashed border-slate-300 px-3 py-2 hover:border-slate-500">
        <Upload size={14} />{busy ? "Subiendo..." : single ? "Subir imagen" : "Subir fotografías"}
        <input type="file" accept="image/*" multiple={!single} className="hidden" onChange={add} data-testid={`${tid}-input`} />
      </label>
    </div>
  );
}

function Files({ value = [], onChange, tid }) {
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    setBusy(true);
    const up = await uploadFiles([...e.target.files], "private");
    setBusy(false);
    onChange([...(value || []), ...up.map((u) => ({ name: u.name, path: u.path, url: u.url, at: new Date().toISOString().slice(0, 10) }))]);
  };
  return (
    <div className="space-y-2" data-testid={tid}>
      {(value || []).map((f, i) => (
        <div key={i} className="flex items-center justify-between text-sm bg-slate-50 rounded-md px-3 py-2">
          <a href={`${fileUrl(f.url, true)}&download=1`} className="flex items-center gap-2 text-slate-700 hover:underline truncate"><FileText size={14} />{f.name}</a>
          <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))}><X size={14} /></button>
        </div>
      ))}
      <label className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer rounded-md border border-dashed border-slate-300 px-3 py-2">
        <Upload size={14} />{busy ? "Subiendo..." : "Adjuntar documentos"}
        <input type="file" multiple className="hidden" onChange={add} data-testid={`${tid}-input`} />
      </label>
    </div>
  );
}

function RefSelect({ field, value, onChange, tid }) {
  const items = useRef(field.ref);
  const lk = field.labelKey || "name";
  return (
    <select className={sel} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
      <option value="">— Ninguno —</option>
      {items.map((x) => <option key={x.id} value={x.id}>{field.ref === "_users" ? (x.name || x.email) : [x.code, x[lk]].filter(Boolean).join(" · ")}</option>)}
    </select>
  );
}

function ListField({ field, value = [], onChange, tid }) {
  const list = value || [];
  const upd = (i, k, v) => onChange(list.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
  return (
    <div className="space-y-2" data-testid={tid}>
      {list.map((row, i) => (
        <div key={i} className="flex gap-2 items-start bg-slate-50 rounded-lg p-2">
          <div className="grid gap-2 flex-1" style={{ gridTemplateColumns: `repeat(${Math.min(field.fields.length, 3)}, minmax(0,1fr))` }}>
            {field.fields.map((sf) => (
              <FieldInput key={sf.key} field={{ ...sf, compact: true }} value={row[sf.key]} onChange={(v) => upd(i, sf.key, v)} tid={`${tid}-${i}-${sf.key}`} />
            ))}
          </div>
          <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, {}])} className="inline-flex items-center gap-1 text-sm font-medium text-[#FF3333]" data-testid={`${tid}-add`}><Plus size={14} />Añadir</button>
    </div>
  );
}

export function FieldInput({ field: f, value, onChange, tid }) {
  const t = f.type || "text";
  if (t === "textarea") return <Textarea rows={f.rows || 4} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder || (f.compact ? f.label : "")} data-testid={tid} />;
  if (t === "select") return (
    <select className={sel} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
      <option value="">{f.compact ? f.label : "Selecciona"}</option>
      {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
  if (t === "checkbox") return <Switch checked={!!value} onCheckedChange={onChange} data-testid={tid} />;
  if (t === "tags") return <Input value={(value || []).join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trimStart()).filter((s, i, a) => s || i === a.length - 1))} onBlur={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="Separadas por comas" data-testid={tid} />;
  if (t === "ref") return <RefSelect field={f} value={value} onChange={onChange} tid={tid} />;
  if (t === "images") return <Images value={value} onChange={onChange} tid={tid} />;
  if (t === "image") return <Images single value={value} onChange={onChange} tid={tid} />;
  if (t === "files") return <Files value={value} onChange={onChange} tid={tid} />;
  if (t === "list") return <ListField field={f} value={value} onChange={onChange} tid={tid} />;
  return <Input type={t} step={t === "number" ? "any" : undefined} value={value ?? ""} placeholder={f.placeholder || (f.compact ? f.label : "")} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
}

export function FormGrid({ fields, value, onChange, prefix }) {
  return (
    <div className="grid sm:grid-cols-2 gap-x-5 gap-y-4">
      {fields.filter((f) => !f.showIf || f.showIf(value)).map((f) => (
        <label key={f.key} className={`block space-y-1.5 ${f.full || ["textarea", "images", "files", "list"].includes(f.type) ? "sm:col-span-2" : ""}`}>
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{f.label}{f.required && " *"}</span>
          <FieldInput field={f} value={value[f.key]} onChange={(v) => onChange({ ...value, [f.key]: v })} tid={`${prefix}-field-${f.key}`} />
          {f.hint && <span className="text-xs text-slate-400">{f.hint}</span>}
        </label>
      ))}
    </div>
  );
}
